import { Controller } from "@hotwired/stimulus"

export default class ImportMonitorController extends Controller {
  static targets = [
    "statusBadge", "progressBar", "progressText", "etaText",
    "rateText", "elapsedText", "errorCount", "refreshHint"
  ]
  static values = { url: String, active: Boolean }

  connect() {
    if (this.activeValue) {
      this.poll()
    }
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  poll() {
    fetch(this.urlValue, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => this.update(data))
      .catch(() => {})
      .finally(() => {
        if (this.activeValue) {
          this._timer = setTimeout(() => this.poll(), 3000)
        }
      })
  }

  update(data) {
    // Status badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = this.capitalize(data.status)
      this.statusBadgeTarget.className = `status-badge status-${data.status}`
    }

    // Progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress_percentage}%`
    }

    // Progress text
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent =
        `${this.formatNumber(data.processed_rows)} / ${this.formatNumber(data.total_rows)} rows (${data.progress_percentage}%)`
    }

    // Rows per second
    if (this.hasRateTextTarget && data.rows_per_second !== null) {
      this.rateTextTarget.textContent = `${this.formatNumber(data.rows_per_second)} rows/sec`
    }

    // Elapsed time
    if (this.hasElapsedTextTarget && data.elapsed_seconds !== null) {
      this.elapsedTextTarget.textContent = this.formatDuration(data.elapsed_seconds)
    }

    // ETA
    if (this.hasEtaTextTarget) {
      this.etaTextTarget.textContent = data.eta_display || "Calculating..."
    }

    // Error count
    if (this.hasErrorCountTarget) {
      this.errorCountTarget.textContent = this.formatNumber(data.error_rows)
    }

    // Stop polling when done
    if (data.status === "completed" || data.status === "failed" || data.status === "cancelled") {
      this.activeValue = false
      if (this.hasRefreshHintTarget) {
        const messages = {
          completed: "Import completed successfully.",
          failed: "Import failed. Check the error log below.",
          cancelled: "Import was cancelled."
        }
        this.refreshHintTarget.textContent = messages[data.status]
      }
      // Reload page once to show final state (error log, completion time, etc.)
      window.location.reload()
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  formatNumber(n) {
    if (n === null || n === undefined) return "0"
    return Number(n).toLocaleString()
  }

  formatDuration(totalSeconds) {
    if (totalSeconds === null) return "--"
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = Math.floor(totalSeconds % 60)
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }
}
